import { atom, useAtom } from 'jotai';
import { useCallback } from 'react';
import { useToast } from '@/component/Toast';
import { captureException } from '@/feature/sentry';
import { useTexts } from '../lang';
import { continueConversation, startConversation } from './api';
import {
  ConversationBaseDto,
  ConversationDto,
  ConversationMessageDto,
  StartConversationRequest,
} from './api.types';
import { ChatMessage } from './types';
import { useHasChatbotPermit } from './useChatbotPermit';
import { ChatContext, useChatContext } from './useChatContext';
import { useConversation } from './useConversation';
import { useStreamedResponseHandler } from './useStreamedResponseHandler';

interface ChatbotProps {
  chatContext: ChatContext;
  conversation?: ConversationBaseDto;
  messages: ChatMessage[];
  isLoading: boolean;
  isAvailable: boolean;
  isOpen: boolean;
  isChatEmpty: boolean;
  setIsOpen: (open: boolean) => void;
  handleUserInput: (text: string) => void;
  createNew: () => void;
  assignConversation: (conversation: ConversationDto) => void;
  setMessage: (message: Partial<ChatMessage>) => void;
}

const isOpenAtom = atom<boolean>(false);
const isLoadingAtom = atom<boolean>(false);
const messagesAtom = atom<ChatMessage[]>([]);

const equals = (a: Partial<ChatMessage>, b: Partial<ChatMessage>) =>
  Boolean(
    (a.id && a.id === b.id) ||
    (a.temporaryId && a.temporaryId === b.temporaryId)
  );

const setMessageAtom = atom(null, (get, set, message: Partial<ChatMessage>) => {
  const messages = get(messagesAtom);
  if (messages.find((m) => equals(m, message))) {
    set(
      messagesAtom,
      messages.map((m) => (equals(m, message) ? (message as ChatMessage) : m))
    );
  } else {
    set(
      messagesAtom,
      get(messagesAtom).concat({
        ...message,
        id: message.id ?? crypto.randomUUID(),
        temporaryId: message.temporaryId,
        timestamp: message.timestamp ?? new Date(),
      } as ChatMessage)
    );
  }
});

export const useChatbot = (): ChatbotProps => {
  const { conversation, setConversation } = useConversation();
  const [messages, setMessages] = useAtom(messagesAtom);
  const [, setMessage] = useAtom(setMessageAtom);
  const [isOpenState, setIsOpen] = useAtom(isOpenAtom);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const isAvailable = useIsAvailable();
  const isOpen = isOpenState && isAvailable;
  const chatContext = useChatContext({ isOpen, conversation });
  const toast = useToast();
  const texts = useTexts().component.chatbot.error;

  const handleResponse = useStreamedResponseHandler(
    chatContext,
    setConversation,
    setMessage
  );

  const handleUserInput = async (input: string) => {
    setIsLoading(true);
    setMessage({ sender: 'user', content: input });

    try {
      if (conversation) {
        const apiResponse = await continueConversation(conversation.id, input);
        handleResponse(apiResponse);
      } else {
        const topicQuery: StartConversationRequest = {
          topicId: `manuals|${chatContext.equipmentId}`,
          query: input,
          embeddingOnly: false,
          translate: true,
          referenceLookupStrategy: 'tool',
          referenceType: 'pages',
        };

        const apiResponse = await startConversation(topicQuery);

        handleResponse(apiResponse);
      }
    } catch (error) {
      captureException(error);
      toast.error(texts.technicalError);
    }

    setIsLoading(false);
  };

  const createNew = useCallback(() => {
    setConversation(undefined);
    setMessages([]);
    setIsLoading(false);
  }, [setConversation, setMessages, setIsLoading]);

  const assignConversation = (conversation: ConversationDto) => {
    setConversation(conversation);
    setMessages(getChatMessages(conversation));
  };

  const isChatEmpty = messages.length === 0;

  return {
    chatContext,
    conversation,
    messages,
    isLoading,
    isAvailable,
    isOpen,
    isChatEmpty,
    setIsOpen,
    handleUserInput,
    createNew,
    assignConversation,
    setMessage,
  };
};

const useIsAvailable = () => useHasChatbotPermit();

const getChatMessages = (conversation: ConversationDto): ChatMessage[] =>
  conversation.history?.reduce(
    (
      messages: ChatMessage[],
      apiMessage: ConversationMessageDto,
      index,
      history
    ) => {
      messages.push({
        id: apiMessage.id,
        sender: apiMessage.type === 'ai' ? 'assistant' : 'user',
        timestamp: new Date(apiMessage.createdAt),
        content: apiMessage.message,
        rating: apiMessage.rating,
      });
      const references = getReferences(history, index, apiMessage);
      if (references.length) {
        messages.push({
          id: apiMessage.id + '_ref',
          sender: 'assistant',
          timestamp: new Date(apiMessage.createdAt),
          isReference: true,
          references,
        });
      }

      return messages;
    },
    []
  ) ?? [];

const getReferences = (
  history: ConversationMessageDto[],
  index: number,
  apiMessage: ConversationMessageDto
) => {
  const prevApiMessage = history[index - 1];
  const isLastMessage = index + 1 === history.length;
  const references = [
    ...(prevApiMessage?.type === 'user'
      ? (prevApiMessage?.references ?? [])
      : []),
    ...(isLastMessage || apiMessage.type === 'ai'
      ? (apiMessage.references ?? [])
      : []),
  ];
  return references;
};
